import {
  type PatientConsentDocumentsResponse,
  type PatientMessageChannel,
  type PatientMessagingChannelConsent,
  type PatientMessagingConsent,
  type PatientMessagingDecisionValue,
  type RecordPatientMessagingDecisionRequest,
  type RecordPatientMessagingDecisionResponse,
  type StaffMessagingDecisionChannel,
  StaffMessagingDecisionInputSchema,
} from "@emr/contracts";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { type PatientConsentsClient, patientConsentDocumentsKey } from "../../api/patientConsents";
import type { PatientMessagingClient } from "../../api/patientMessaging";
import { ConsentDocumentText } from "../../components/ConsentDocumentText";
import { createDemoSchedulingIdempotencyKey } from "../../demo/DemoSchedulingProvider";
import { SchedulingRequestError, schedulingErrorDetail } from "../../demo/schedulingErrors";
import {
  MESSAGING_CONSENT_STATE_LABELS,
  messagingConsentStateText,
  messagingConsentStateTone,
  messagingDecisionPhrase,
  messagingDecisionRefusalText,
  PATIENT_MESSAGE_CHANNEL_PLURALS,
} from "./patientMessageCopy";
import { formatInClinic } from "./scheduleTime";
import "./patient-messages.css";

/** Where a staff decision is recorded: a booking (a walk-in's row included) or the chart. */
export type MessagingDecisionTarget =
  | { readonly kind: "appointment"; readonly appointmentId: string }
  | { readonly kind: "patient"; readonly patientId: string };

/** The routes that record a decision, and the notice staff read to the patient first. */
export type MessagingConsentClient = Pick<
  PatientMessagingClient,
  "recordAppointmentMessagingDecision" | "recordPatientMessagingDecision"
> &
  Pick<PatientConsentsClient, "getPatientConsentDocuments">;

const CHANNELS = ["sms", "email"] as const satisfies readonly PatientMessageChannel[];
const CHANNEL_TITLES: Readonly<Record<PatientMessageChannel, string>> = {
  sms: "Texts",
  email: "Emails",
};

/**
 * The clinic's text and email notice for a staff decision: its current version and whether the
 * clinic offers messaging. Not an audited read; shared with the admin page's cache.
 */
export function useMessagingNotice(
  client: Pick<PatientConsentsClient, "getPatientConsentDocuments"> | undefined,
  siteId: string,
  enabled: boolean,
) {
  return useQuery<PatientConsentDocumentsResponse>({
    queryKey: patientConsentDocumentsKey(siteId),
    queryFn: ({ signal }) => {
      if (client === undefined) throw new Error("The clinic's notice is unavailable.");
      return client.getPatientConsentDocuments(signal);
    },
    enabled: enabled && client !== undefined,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** One line on who decided and when ("Agreed on the phone · Oct 8, 10:20 a.m. · Dana Desk"). */
function decisionLine(
  channelConsent: PatientMessagingChannelConsent,
  timeZone: string,
  target: MessagingDecisionTarget,
): string | null {
  const decision = channelConsent.decision;
  if (decision === null) return null;
  return [
    messagingDecisionPhrase(decision.decision, decision.source),
    formatInClinic(decision.decidedAt, timeZone, { dateStyle: "medium", timeStyle: "short" }),
    ...(decision.recordedByName === null ? [] : [`recorded by ${decision.recordedByName}`]),
    ...(decision.documentVersion === null ? [] : [`notice version ${decision.documentVersion}`]),
    ...(decision.appointmentId === null
      ? ["on the chart"]
      : target.kind === "appointment" && decision.appointmentId !== target.appointmentId
        ? ["on another visit"]
        : []),
  ].join(" · ");
}

function refusalText(caught: unknown): string {
  const known = messagingDecisionRefusalText(schedulingErrorDetail(caught));
  if (known !== undefined) return known;
  if (caught instanceof SchedulingRequestError) {
    if (caught.status === 401 || caught.status === 403) {
      return "Your role can't record this patient's answer about texts and emails.";
    }
    return caught.message;
  }
  return "The answer could not be recorded. Try again.";
}

/**
 * Whether the clinic may text or email the patient (PA-8), channel by channel, with the decision
 * that settled it, and the desk's **Patient agreed** / **Patient said no** (owner decision O5).
 * An agreement is recorded only after staff read the clinic's current text and email notice to
 * the patient, and only while the clinic offers a channel; a refusal is always taken and also
 * withdraws an earlier agreement. A STOP is shown but never lifted here: only the patient
 * restarts texts, by replying START.
 *
 * On a booking the decision covers the patient's visits at this clinic once the booking is
 * linked to a chart (owner decision O1); on the chart it covers the chart. Outcomes and refusals
 * are written on the panel, never above it. Shared by the booking's Texts and emails, the chart's
 * Consent card and the walk-in dialog's chart, so it carries its own --pm-* tokens.
 */
export function MessagingConsentPanel({
  target,
  siteId,
  consent,
  offeredChannels,
  timeZone,
  client,
  heardBy = "front_desk",
  onRecorded,
}: {
  readonly target: MessagingDecisionTarget;
  readonly siteId: string;
  readonly consent: PatientMessagingConsent;
  readonly offeredChannels: readonly PatientMessageChannel[];
  readonly timeZone: string;
  /** Absent: the states are shown read-only. */
  readonly client?: MessagingConsentClient | undefined;
  /** How the desk most likely heard it (a phone booking: on the phone). */
  readonly heardBy?: StaffMessagingDecisionChannel;
  /** After a decision is recorded, so the host reads its states again. */
  readonly onRecorded?: ((response: RecordPatientMessagingDecisionResponse) => void) | undefined;
}) {
  const headingId = useId();
  const [asking, setAsking] = useState<PatientMessagingDecisionValue | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const noteLine = useRef<HTMLParagraphElement>(null);
  const agreeButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const wasAsking = useRef(false);
  useEffect(() => {
    if (asking !== null) {
      wasAsking.current = true;
      return;
    }
    if (!wasAsking.current) return;
    wasAsking.current = false;
    // The form, and the button focused in it, is gone: focus its outcome, or what opened it.
    (noteLine.current ?? opener.current ?? agreeButton.current)?.focus();
  }, [asking]);
  const offered = offeredChannels.length > 0;
  const inactive = CHANNELS.every((channel) => consent[channel].state === "chart_inactive");

  return (
    <section className="pm pm-consent" aria-labelledby={headingId}>
      <div className="pm-row-main">
        <h4 id={headingId}>Agreement to texts and emails</h4>
      </div>
      <ul className="pm-list">
        {CHANNELS.map((channel) => {
          const channelConsent = consent[channel];
          const line = decisionLine(channelConsent, timeZone, target);
          const stateText = messagingConsentStateText(channelConsent.state, channel);
          return (
            <li key={channel} className="pm-row" data-channel={channel}>
              <div className="pm-row-main">
                <span className="pm-row-title">{CHANNEL_TITLES[channel]}</span>
                <span className={`pm-chip tone-${messagingConsentStateTone(channelConsent.state)}`}>
                  {MESSAGING_CONSENT_STATE_LABELS[channelConsent.state]}
                </span>
                {!offeredChannels.includes(channel) && channelConsent.state !== "chart_inactive" ? (
                  <span className="pm-chip tone-muted">Not sent by the clinic</span>
                ) : null}
              </div>
              {line !== null ? <p className="pm-row-meta">{line}</p> : null}
              {stateText !== null ? <p className="pm-row-why">{stateText}</p> : null}
            </li>
          );
        })}
      </ul>
      {!offered ? (
        <p className="pm-quiet">
          The clinic doesn't send texts or emails yet, so staff can't record an agreement. A refusal
          can be recorded.
        </p>
      ) : null}
      {client !== undefined && !inactive && asking === null ? (
        <div className="pm-row-actions">
          <button
            ref={agreeButton}
            type="button"
            className="pm-btn ghost sm"
            disabled={!offered}
            onClick={(event) => {
              opener.current = event.currentTarget;
              setNote(null);
              setAsking("accepted");
            }}
          >
            Patient agreed
          </button>
          <button
            type="button"
            className="pm-btn ghost sm"
            onClick={(event) => {
              opener.current = event.currentTarget;
              setNote(null);
              setAsking("declined");
            }}
          >
            Patient said no
          </button>
        </div>
      ) : null}
      {client !== undefined && asking !== null ? (
        <MessagingDecisionForm
          key={asking}
          decision={asking}
          target={target}
          siteId={siteId}
          offeredChannels={offeredChannels}
          heardBy={heardBy}
          client={client}
          onCancel={() => setAsking(null)}
          onRecorded={(response, text) => {
            setNote(text);
            setAsking(null);
            onRecorded?.(response);
          }}
        />
      ) : null}
      {note !== null ? (
        <p ref={noteLine} className="pm-row-note" role="status" tabIndex={-1}>
          {note}
        </p>
      ) : null}
    </section>
  );
}

/** What the desk heard while booking: nothing yet, or the patient's answer on a notice version. */
export interface BookingMessagingAnswer {
  readonly decision: PatientMessagingDecisionValue;
  /** The notice version on screen; null while it loads (an agreement then waits for it). */
  readonly documentVersion: number | null;
}

/**
 * "Texts and emails" in Schedule's staff booking form (PA-8, durable clinics only): Not asked
 * (the default), Patient agreed or Patient said no, with the clinic's notice to read first. The
 * answer is recorded in the booking's own transaction, so the confirmation it plans is sent (or
 * withheld) on it. Shown only while the clinic offers texts or emails (lead decision K3).
 */
export function BookingMessagingField({
  siteId,
  client,
  answer,
  onAnswer,
  disabled = false,
}: {
  readonly siteId: string;
  readonly client: Pick<PatientConsentsClient, "getPatientConsentDocuments">;
  readonly answer: BookingMessagingAnswer | null;
  readonly onAnswer: (answer: BookingMessagingAnswer | null) => void;
  readonly disabled?: boolean;
}) {
  const legendId = useId();
  const name = useId();
  const notice = useMessagingNotice(client, siteId, true);
  const current = notice.data?.electronicMessaging.current;
  const version = current?.version ?? null;
  // A newer notice read while the form is open replaces the version the answer names.
  useEffect(() => {
    if (answer !== null && answer.documentVersion !== version && version !== null) {
      onAnswer({ ...answer, documentVersion: version });
    }
  }, [answer, onAnswer, version]);
  if (notice.data === undefined || !notice.data.messagingOffered) return null;
  const choose = (decision: PatientMessagingDecisionValue | null) =>
    onAnswer(decision === null ? null : { decision, documentVersion: version });
  return (
    <section className="pm pm-consent pm-booking-messaging" aria-labelledby={legendId}>
      <fieldset className="pm-consent-choices" disabled={disabled}>
        <legend id={legendId}>Texts and emails</legend>
        <label className="pm-check">
          <input type="radio" name={name} checked={answer === null} onChange={() => choose(null)} />
          Not asked
        </label>
        <label className="pm-check">
          <input
            type="radio"
            name={name}
            checked={answer?.decision === "accepted"}
            onChange={() => choose("accepted")}
          />
          Patient agreed
        </label>
        <label className="pm-check">
          <input
            type="radio"
            name={name}
            checked={answer?.decision === "declined"}
            onChange={() => choose("declined")}
          />
          Patient said no
        </label>
      </fieldset>
      <p className="pm-quiet">
        Patients get the booking confirmation and reminders by text or email only if they agree.
        {answer?.decision === "accepted" ? " Read them the clinic's notice first." : ""}
      </p>
      {answer?.decision === "accepted" && current !== undefined ? (
        <details className="pm-consent-text">
          <summary>
            Read the notice: {current.title} (version {current.version})
          </summary>
          <section
            className="pm-consent-scroll"
            aria-label={`${current.title}, version ${current.version}`}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: the notice scrolls; keyboard users need a focusable region to scroll it.
            tabIndex={0}
          >
            <ConsentDocumentText body={current.body} />
          </section>
        </details>
      ) : null}
    </section>
  );
}

/**
 * The desk's form for one decision, on the panel: which channels, how the patient said it, and
 * for an agreement the notice to read first. One idempotency key per form: a retry after a
 * dropped connection never records twice.
 */
function MessagingDecisionForm({
  decision,
  target,
  siteId,
  offeredChannels,
  heardBy,
  client,
  onCancel,
  onRecorded,
}: {
  readonly decision: PatientMessagingDecisionValue;
  readonly target: MessagingDecisionTarget;
  readonly siteId: string;
  readonly offeredChannels: readonly PatientMessageChannel[];
  readonly heardBy: StaffMessagingDecisionChannel;
  readonly client: MessagingConsentClient;
  readonly onCancel: () => void;
  readonly onRecorded: (response: RecordPatientMessagingDecisionResponse, note: string) => void;
}) {
  const titleId = useId();
  const firstField = useRef<HTMLInputElement>(null);
  const agreeing = decision === "accepted";
  const notice = useMessagingNotice(client, siteId, agreeing);
  const [channels, setChannels] = useState<ReadonlySet<PatientMessageChannel>>(
    () => new Set(agreeing ? offeredChannels : CHANNELS),
  );
  const [how, setHow] = useState<StaffMessagingDecisionChannel>(heardBy);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useRef(createDemoSchedulingIdempotencyKey("messaging-decision"));
  useEffect(() => {
    firstField.current?.focus();
  }, []);
  const current = notice.data?.electronicMessaging.current;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const messageChannels = CHANNELS.filter((channel) => channels.has(channel));
    const parsed = StaffMessagingDecisionInputSchema.safeParse({
      decision,
      channel: how,
      messageChannels,
      ...(agreeing && current !== undefined ? { documentVersion: current.version } : {}),
    });
    if (!parsed.success) {
      setError(
        messageChannels.length === 0
          ? "Choose texts, emails or both."
          : "Read the clinic's notice to the patient first; it is still loading.",
      );
      return;
    }
    const request: RecordPatientMessagingDecisionRequest = {
      ...parsed.data,
      idempotencyKey: key.current,
    };
    setSaving(true);
    setError(null);
    try {
      const response =
        target.kind === "appointment"
          ? await client.recordAppointmentMessagingDecision(target.appointmentId, request)
          : await client.recordPatientMessagingDecision(target.patientId, request);
      const what = messageChannels.map((channel) => PATIENT_MESSAGE_CHANNEL_PLURALS[channel]);
      onRecorded(
        response,
        agreeing
          ? `Recorded: the patient agreed to ${what.join(" and ")}.`
          : `Recorded: the patient said no to ${what.join(" and ")}. Nothing more is sent on ${
              messageChannels.length === 1 ? "that channel" : "them"
            }.`,
      );
    } catch (caught) {
      // A refusal (4xx) is final for this key; a dropped connection may have recorded it.
      if (caught instanceof SchedulingRequestError && caught.status < 500) {
        key.current = createDemoSchedulingIdempotencyKey("messaging-decision");
      }
      if (schedulingErrorDetail(caught) === "messaging_notice_changed") {
        await notice.refetch();
      }
      setError(refusalText(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="pm-confirm pm-consent-form"
      aria-labelledby={titleId}
      noValidate
      onSubmit={(event) => void submit(event)}
    >
      <p id={titleId} className="pm-row-title">
        {agreeing ? "Record that the patient agreed" : "Record that the patient said no"}
      </p>
      {agreeing ? (
        <div className="pm-consent-notice">
          {notice.isPending ? (
            <p className="pm-quiet" role="status">
              Loading the clinic's notice…
            </p>
          ) : null}
          {notice.isError ? (
            <p className="pm-row-error" role="alert">
              The clinic's notice could not be loaded, so an agreement can't be recorded.{" "}
              <button
                type="button"
                className="pm-btn ghost sm"
                onClick={() => void notice.refetch()}
              >
                Try again
              </button>
            </p>
          ) : null}
          {current !== undefined ? (
            <>
              <p className="pm-row-why">
                Read the clinic's notice to the patient before recording their agreement.
              </p>
              <details className="pm-consent-text">
                <summary>
                  Read the notice: {current.title} (version {current.version})
                </summary>
                <section
                  className="pm-consent-scroll"
                  aria-label={`${current.title}, version ${current.version}`}
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: the notice scrolls; keyboard users need a focusable region to scroll it.
                  tabIndex={0}
                >
                  <ConsentDocumentText body={current.body} />
                </section>
              </details>
            </>
          ) : null}
        </div>
      ) : (
        <p className="pm-row-why">
          This also withdraws an earlier agreement. Messages already sent stay in the record.
        </p>
      )}
      <fieldset className="pm-consent-choices" disabled={saving}>
        <legend>{agreeing ? "They agreed to" : "They said no to"}</legend>
        {CHANNELS.map((channel, index) => (
          <label key={channel} className="pm-check">
            <input
              ref={index === 0 ? firstField : undefined}
              type="checkbox"
              checked={channels.has(channel)}
              onChange={(event) => {
                const next = new Set(channels);
                if (event.target.checked) next.add(channel);
                else next.delete(channel);
                setChannels(next);
                setError(null);
              }}
            />
            {CHANNEL_TITLES[channel]}
            {agreeing && !offeredChannels.includes(channel) ? (
              <span className="pm-check-hint">(once the clinic sends them)</span>
            ) : null}
          </label>
        ))}
      </fieldset>
      <fieldset className="pm-consent-choices" disabled={saving}>
        <legend>How they told you</legend>
        <label className="pm-check">
          <input
            type="radio"
            name={`${titleId}-how`}
            checked={how === "front_desk"}
            onChange={() => setHow("front_desk")}
          />
          At the front desk
        </label>
        <label className="pm-check">
          <input
            type="radio"
            name={`${titleId}-how`}
            checked={how === "phone"}
            onChange={() => setHow("phone")}
          />
          On the phone
        </label>
      </fieldset>
      {error !== null ? (
        <p className="pm-row-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="pm-actions">
        <button type="button" className="pm-btn ghost sm" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="pm-btn primary sm"
          disabled={saving || (agreeing && current === undefined)}
        >
          {saving ? "Recording…" : agreeing ? "Record agreement" : "Record refusal"}
        </button>
      </div>
    </form>
  );
}
